import { Box, Flex, Image } from '@chakra-ui/react';
import { VStack, Heading, Text, Button, Link } from 'components/ui';

import styled from '@emotion/styled';
import { EmotionTheme } from 'theme/emotionTheme';
import {
  AffiliateCareModel,
  AffiliateType,
} from '../../../../models/affiliate';
import { useAffiliate } from '../../../../api/affiliates/useAffiliate';
import { TRACKING_LANDING_PAGE_CTA } from '../../../OnboardingForm/constants';

const gif = '/new_look.gif';

const GradientBox = styled('div')<{ theme?: EmotionTheme }>`
  background-image: linear-gradient(180deg, #043338 0%, #2d6670 100%);
  padding-left: 24px;
  padding-right: 24px;
  text-align: center;
  border-top-left-radius: 32px;
  border-top-right-radius: 32px;
`;

const headerText = (affiliate) => {
  if (!affiliate) return 'Affordable Digestive Care, Covered by Insurance';
  if (affiliate?.landing_page_middle_header) {
    return affiliate?.landing_page_middle_header;
  } else if (affiliate?.affiliate_type === AffiliateType.healthSystem) {
    return 'Covered By Insurance';
  } else if (affiliate?.affiliate_type === AffiliateType.employer) {
    return 'Ready to take your first step to digestive relief?';
  } else {
    return `Now In-network for ${affiliate?.name} Members!`;
  }
};

const renderContent = (affiliate) => {
  if (affiliate?.landing_page_middle_body) {
    return affiliate?.landing_page_middle_body;
  } else if (affiliate?.affiliate_type === AffiliateType.healthSystem) {
    return `Ayble supports ${affiliate?.name} members, whether they may be dealing with occasional digestive symptoms or chronic conditions like IBS, Crohn’s Disease, Ulcerative Colitis, GERD, SIBO, and more.`;
  } else if (affiliate?.affiliate_type === AffiliateType.employer) {
    return `Ayble supports you, whether you're dealing with occasional digestive symptoms or chronic conditions like IBS, Crohn's Disease, Ulcerative Colitis, GERD, SIBO, and more.`;
  } else {
    // default - health plan
    if (affiliate?.care_model === AffiliateCareModel.collaborative) {
      return 'Get ongoing digestive wellness support from Ayble, right from home. Access personalized GI nutrition and stress management plans, connect with your dedicated care team, and receive help implementing your provider’s recommendations — all through your health plan benefits.\n\nFor virtual visits with our GI care team, you will pay your virtual visit cost share.';
    } else {
      return `Get ongoing digestive wellness support from Ayble, right from home. Access personalized GI nutrition and stress management plans, connect with your dedicated care team, and receive help implementing your provider’s recommendations — all covered by your insurance. Take control of your gut health today, without the extra cost.`;
    }
  }
};

export const InNetwork = ({ onStartOnboarding }) => {
  const { affiliate } = useAffiliate();

  return (
    <GradientBox>
      <Flex
        direction={{ base: 'column', lg: 'row-reverse' }}
        alignItems={'center'}
        mx={'auto'}
        maxW={'1000px'}
      >
        <VStack
          gap={8}
          alignItems={{ base: 'center', lg: 'auto' }}
          flex={1}
          maxW={{ base: '100%', lg: '43%' }}
        >
          <Heading
            color={'white'}
            fontSize={'4xl'}
            fontWeight={400}
            textAlign={{ base: 'center', lg: 'left' }}
            mt={{ base: 10, lg: 0 }}
          >
            {headerText(affiliate)}
          </Heading>

          {!affiliate ||
          affiliate?.affiliate_type === AffiliateType.healthSystem ? (
            <Image
              alt="insurance logos"
              src={'insurance_logos.png'}
              w={'417px'}
              h={'156px'}
              objectFit="contain"
            />
          ) : (
            <Text
              color={'white'}
              fontSize={{ base: '16px', lg: 'lg' }}
              textAlign={{ base: 'center', lg: 'left' }}
              fontWeight={500}
            >
              {renderContent(affiliate)}
            </Text>
          )}

          <Box w={{ base: '100%', lg: '80%' }} mt={8}>
            <Button
              w="100%"
              backgroundColor={'#5299A9'}
              color="white"
              textTransform="none"
              fontSize="md"
              onClick={() => onStartOnboarding(TRACKING_LANDING_PAGE_CTA)}
            >
              Get Started Today →
            </Button>
            <Text
              textAlign={'center'}
              color="#FFDA67"
              fontWeight={700}
              fontSize={'14px'}
              mt={5}
            >
              Questions? Email{' '}
              <Link
                href="mailto:support@ayblehealth.com"
                mb={5}
                _text={{
                  color: '#FFDA67',
                  textTransform: 'none',
                  fontWeight: '700',
                  fontSize: '14px',
                }}
              >
                support@ayblehealth.com
              </Link>
            </Text>
          </Box>
        </VStack>
        <Box flex={1}>
          <Image
            src={gif}
            h={{ base: 600, lg: 678 }}
            w={452}
            objectFit="contain"
            alt="Ayble new look"
          />
        </Box>
      </Flex>
    </GradientBox>
  );
};
